"use client";

import { useEffect, useRef } from "react";

function ComplianceCard({
    icon,
    title,
    summary,
}: {
    icon: React.ReactNode;
    title: string;
    summary: string;
}) {
    return (
        <div className="card p-6 flex flex-col gap-4">
            <div
                className="icon-box"
                style={{
                    background: "var(--color-navy)",
                    borderRadius: "4px",
                    width: "48px",
                    height: "48px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                }}
            >
                {icon}
            </div>
            <h3
                style={{
                    fontFamily: "var(--font-serif)",
                    fontSize: "1.3125rem",
                    fontWeight: 400,
                    color: "var(--color-white)",
                    lineHeight: 1.2,
                }}
            >
                {title}
            </h3>
            <p className="type-body">{summary}</p>
        </div>
    );
}

export default function AboutHeader() {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const els = ref?.current?.querySelectorAll(".reveal");
        if (!els) return;
        const observer = new IntersectionObserver(
            (entries) =>
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        e.target.classList.add("visible");
                        observer.unobserve(e.target);
                    }
                }),
            { threshold: 0.1 }
        );
        els?.forEach((el) => observer?.observe(el));
        return () => observer?.disconnect();
    }, []);

    return (
        <section
            ref={ref}
            className="section-navy-mid"
            style={{ paddingTop: "80px", paddingBottom: "80px" }}
        >
            <div
                className="px-6 lg:px-12"
                style={{ maxWidth: "1280px", margin: "0 auto" }}
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12 reveal reveal-delay-1">
                    <ComplianceCard
                        icon={
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-white)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: "var(--color-red)", width: "24px", height: "24px" }}>
                                <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                                <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                                <path d="m9 15 2 2 4-4"></path>
                            </svg>
                        }
                        title="Chain-of-Custody"
                        summary="Full chain-of-custody documentation and origin declarations for every shipment."
                    />
                    <ComplianceCard
                        icon={
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-white)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: "var(--color-red)", width: "24px", height: "24px" }}>
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                                <path d="m9 12 2 2 4-4"></path>
                            </svg>
                        }
                        title="H&S Management"
                        summary="Health & safety management controls aligned with international standards."
                    />
                    <ComplianceCard
                        icon={
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-white)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: "var(--color-red)", width: "24px", height: "24px" }}>
                                <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z"></path>
                                <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"></path>
                            </svg>
                        }
                        title="Environmental Compliance"
                        summary="Environmental compliance documentation maintained throughout operations."
                    />
                    <ComplianceCard
                        icon={
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-white)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: "var(--color-red)", width: "24px", height: "24px" }}>
                                <rect width="8" height="4" x="8" y="2" rx="1" ry="1"></rect>
                                <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                                <path d="M12 11h4"></path>
                                <path d="M12 16h4"></path>
                                <path d="M8 11h.01"></path>
                                <path d="M8 16h.01"></path>
                            </svg>
                        }
                        title="Export Controls"
                        summary="Export-control compliance representations with contractual flexibility built in."
                    />
                </div>
            </div>
        </section>
    );
}